/* CalendarViewer.jsx — 2025-06-30 */
import React, {
  useEffect,
  useState,
  useRef,
  useContext,
  useMemo,
} from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import Select from "react-select";
import { FaSearch } from "react-icons/fa";
import "react-datepicker/dist/react-datepicker.css";

import appContext from "../../../context/app/appContext";

/* ───────────────────────────────────────── helpers ────────────────────────── */
const getFontColorForBackground = (hex = "E0E0E0") => {
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.substr(i, 2), 16));
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.5 ? "black" : "white";
};

/* ───────────────────────────────── component ──────────────────────────────── */
export default function CalendarViewer({ focusEvent, preferredDate }) {
  /* refs & context */
  const calendarRef = useRef(null);
  const lastRangeRef = useRef({ start: null, end: null });
  const latestReqId = useRef(0);            // race-condition guard
  const hasScrolledToFocus = useRef(false); // scroll only once

  const { serverGet, calendarCategories } = useContext(appContext);
  const navigate = useNavigate();

  /* state — ui toggles */
  const [showWeekdaysOnly, setShowWeekdaysOnly] = useState(false);
  const [currentView, setCurrentView] = useState("dayGridMonth");

  /* state — data */
  const [eventsInRange, setEventsInRange] = useState([]); // real events only
  const [visibleEvents, setVisibleEvents] = useState([]); // after filters
  const [selectedCats, setSelectedCats] = useState([]);   // react-select

  /* state — misc */
  const [loading, setLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);

  /* ─────────────── category options (memoised) ─────────────── */
  const categoryOptions = useMemo(() => {
    const base = calendarCategories.map((c) => ({
      value: c.cat_num,
      label: c.cat_desc,
    }));
    return [...base, { value: "focus", label: "Focused Event" }];
  }, [calendarCategories]);

  /* select ALL categories once context ready */
  useEffect(() => {
    if (calendarCategories.length) setSelectedCats(categoryOptions);
  }, [calendarCategories, categoryOptions]);

  /* ─────────────── fetch events with stale-response guard ───── */
  const fetchEvents = async (startISO, endISO) => {
    if (!calendarCategories.length) return; // wait for cats

    const reqId = ++latestReqId.current;    // bump id
    setLoading(true);

    try {
      const res = await serverGet(
        `/calendar/eventsByRange?start=${startISO}&end=${endISO}`
      );

      // late response? discard.
      if (reqId !== latestReqId.current) return;

      const mapped = res.map((e) => ({
        id: e.calendar_id,
        title: e.event_name,
        start: e.start_date_time,
        end: e.end_date_time,
        allDay: false,
        extendedProps: {
          ...e,
          tags: e.tags ? e.tags.split(",") : [],
          font_colour: getFontColorForBackground(e.text_colour),
        },
      }));

      setEventsInRange(mapped); // focusEvent injected later
    } catch (err) {
      console.error("Calendar fetch failed:", err);
    } finally {
      setLoading(false);
    }
  };

  /* ─────────────── handle view/range changes ─────────────── */
  const onDatesSet = ({ start, end }, view) => {
    const startISO = moment(start).startOf("day").toISOString();
    const endISO = moment(end).endOf("day").toISOString();

    if (
      startISO !== lastRangeRef.current.start ||
      endISO !== lastRangeRef.current.end
    ) {
      lastRangeRef.current = { start: startISO, end: endISO };
      fetchEvents(startISO, endISO);
    }
    setCurrentView(view.type);
  };

  /* ─────────────── inject focusEvent AFTER data fetched ───── */
  const combinedEvents = useMemo(() => {
    if (!focusEvent) return eventsInRange;

    const focusStart = moment(
      `${focusEvent.preferredDate}T${focusEvent.startTime}`
    ).toISOString();
    const focusEnd = moment(
      `${focusEvent.endDate || focusEvent.preferredDate}T${focusEvent.endTime}`
    ).toISOString();

    return [
      ...eventsInRange,
      {
        id: "focus-event",
        title: focusEvent.eventName,
        start: focusStart,
        end: focusEnd,
        allDay: false,
        extendedProps: {
          ...focusEvent,
          isFocus: true,
          cat_num: "focus",
          font_colour: "black",
        },
      },
    ];
  }, [eventsInRange, focusEvent]);

  /* ─────────────── final category filter ───────────────────── */
  useEffect(() => {
    const allowed = new Set(selectedCats.map((c) => c.value));
    setVisibleEvents(
      combinedEvents.filter((e) => allowed.has(e.extendedProps.cat_num))
    );
  }, [combinedEvents, selectedCats]);

  /* ─────────────── initial mount: goto preferredDate & fetch ─ */
  useEffect(() => {
    const api = calendarRef.current?.getApi?.();
    if (!api || !calendarCategories.length) return;

    // jump to preferredDate once calendar ready
    setTimeout(() => {
      if (preferredDate) api.gotoDate(preferredDate);
    }, 0);

    // fetch for initial visible range
    const { activeStart, activeEnd } = api.view;
    onDatesSet({ start: activeStart, end: activeEnd }, api.view);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferredDate, calendarCategories]);

  /* ────────────────────────────── render ────────────────────── */
  return (
    <div className="p-4 w-full relative">
      {/* top controls */}
      <div className="flex justify-between items-center mb-4">
        {/* left btns */}
        <div className="flex gap-2">
          <button
            onClick={() => calendarRef.current?.getApi()?.today()}
            className="px-3 py-1.5 rounded bg-gray-200 hover:bg-gray-300 text-sm"
          >
            Today
          </button>

          {/* view toggles */}
          {[
            { id: "dayGridMonth", label: "Month View" },
            { id: "timeGridDay", label: "Day View" },
          ].map(({ id, label }) => (
            <button
              key={id}
              onClick={() => calendarRef.current?.getApi()?.changeView(id)}
              className={`px-3 py-1.5 rounded text-sm ${
                currentView === id
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 hover:bg-gray-300"
              }`}
            >
              {label}
            </button>
          ))}

          {currentView === "dayGridMonth" && (
            <label className="text-sm flex items-center gap-2 text-gray-700">
              <input
                type="checkbox"
                checked={showWeekdaysOnly}
                onChange={() => setShowWeekdaysOnly((p) => !p)}
                className="accent-blue-600"
              />
              Weekdays Only
            </label>
          )}
        </div>

        {/* right: jump/search */}
        {!focusEvent && (
          <div className="flex gap-2 items-center">
            <DatePicker
              onChange={(d) => d && calendarRef.current?.getApi()?.gotoDate(d)}
              placeholderText="Jump to date"
              className="border px-3 py-1.5 rounded text-sm"
              dateFormat="dd MMM yyyy"
              isClearable
            />
            <button
              onClick={() => navigate("/calendarSearch")}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded shadow-sm text-sm font-medium"
            >
              <FaSearch />
              Search Calendar
            </button>
          </div>
        )}
      </div>

      {/* category filter */}
      {!focusEvent && (
        <div className="my-4 w-full">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Filter by Category
          </label>
          <Select
            options={categoryOptions}
            value={selectedCats}
            isMulti
            onChange={(sel) => setSelectedCats(sel ?? [])}
            closeMenuOnSelect={false}
            placeholder="Select categories…"
            className="text-sm"
            styles={{
              control: (b) => ({ ...b, minHeight: 38 }),
              menu: (b) => ({ ...b, zIndex: 9999 }),
            }}
          />
          <div className="flex gap-2 mt-2 text-sm">
            <button
              onClick={() => setSelectedCats(categoryOptions)}
              className="px-3 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200"
            >
              Select All
            </button>
            <button
              onClick={() => setSelectedCats([])}
              className="px-3 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              Clear All
            </button>
          </div>
        </div>
      )}

      {/* CALENDAR wrapper */}
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-20">
            <span className="text-gray-700 text-sm">Fetching events…</span>
          </div>
        )}

        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          firstDay={1}
          hiddenDays={showWeekdaysOnly ? [0, 6] : []}
          events={visibleEvents}
          contentHeight="auto"
          headerToolbar={{
            left: "",
            center: "title",
            right: "prev,next",
          }}
          dayCellContent={(arg) => (
            <div className="text-black font-medium">
              {moment(arg.date).format("ddd D MMM")}
            </div>
          )}
          datesSet={(info) => onDatesSet(info, info.view)}
          eventClick={({ event }) => {
            setSelectedEvent({
              ...event.extendedProps,
              title: event.title,
              start: event.start,
              end: event.end,
            });
            setShowModal(true);
          }}
          eventContent={({ event, view }) => {
            const showTime =
              view.type === "dayGridMonth" ||
              moment(event.start).isSame(view.currentStart, "day");

            return (
              <div
                style={{
                  pointerEvents: "none",
                  lineHeight: 1.3,
                  whiteSpace: "normal",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                }}
              >
                {showTime && (
                  <div>
                    <b>{moment(event.start).format("h:mm a")}</b>
                  </div>
                )}
                <div>{event.title}</div>
              </div>
            );
          }}
          eventDidMount={({ el, event }) => {
            el.style.backgroundColor = `#${
              event.extendedProps.text_colour || "E0E0E0"
            }`;
            el.style.color = event.extendedProps.font_colour;
            el.style.borderRadius = "4px";
            el.style.padding = "4px 6px";
            el.style.cursor = "pointer";

            // highlight focusEvent
            if (event.id === "focus-event") el.classList.add("animated-focus");

            // scroll to focusEvent once
            if (event.id === "focus-event" && !hasScrolledToFocus.current) {
              hasScrolledToFocus.current = true;
              setTimeout(() => {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
              }, 500);
            }
          }}
        />
      </div>

      {/* modal */}
      {showModal && selectedEvent && (
        <div className="fixed inset-0 z-50 backdrop-blur-sm bg-black/10 flex items-center justify-center">
          <div className="bg-white shadow-xl rounded-lg max-w-lg w-full p-6 space-y-4 border">
            <h2 className="text-2xl font-semibold text-gray-800 border-b pb-2">
              {selectedEvent.title}
            </h2>

            <div className="space-y-2 text-sm text-gray-700">
              <p>
                <span className="font-medium">Start:</span>{" "}
                {moment(selectedEvent.start).format(
                  "dddd, DD MMM YYYY, h:mm A"
                )}
              </p>
              <p>
                <span className="font-medium">End:</span>{" "}
                {moment(selectedEvent.end).format(
                  "dddd, DD MMM YYYY, h:mm A"
                )}
              </p>

              {selectedEvent.event_desc && (
                <p>
                  <span className="font-medium">Description:</span>{" "}
                  {selectedEvent.event_desc}
                </p>
              )}
              {selectedEvent.room_code && (
                <p>
                  <span className="font-medium">Room Code:</span>{" "}
                  {selectedEvent.room_code}
                </p>
              )}
              {selectedEvent.cat_num && (
                <p>
                  <span className="font-medium">Category Number:</span>{" "}
                  {selectedEvent.cat_num}
                </p>
              )}
              {selectedEvent.entry_code && (
                <p>
                  <span className="font-medium">Entry Code:</span>{" "}
                  {selectedEvent.entry_code}
                </p>
              )}
            </div>

            {selectedEvent.tags?.length > 0 && (
              <div className="pt-3">
                <h3 className="text-sm font-medium text-gray-700 mb-1">Tags:</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedEvent.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800 font-medium border"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              {selectedEvent.event_id && (
                <a
                  href={`/viewEvent/${selectedEvent.event_id}`}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded shadow-sm"
                >
                  View Full Event
                </a>
              )}
              <button
                onClick={() => setShowModal(false)}
                className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-sm font-medium text-gray-800 rounded border"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
